/**
 * @file supplier-ledger-groups.ts
 * @module app/modules/uProcure/vendor-ledger/supplier-ledger-groups
 * @description Supplier ledger / payments helpers: a PO's quantity with its unit ("10 CWP, 5 PCS",
 *   the same numbers the PO was raised with), paid and remaining amounts, and totals per supplier
 *   group (Vendor Profile > Groups; POs without a group belong to the supplier itself).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import Method from "../../../../utils/methods";

export interface LedgerPoGroup {
  reference?: string;
  name?: string;
  contactName?: string;
  phone?: string;
  tinNumber?: string;
  banks?: { bankName?: string; branchName?: string; accountNumber?: string }[];
}

export interface LedgerPo {
  _id: string;
  refKey?: string;
  charge?: number;
  totalQuantities?: { quantityType: number; stockCount: number }[];
  businesses?: { payout?: { paymentMethod?: number; totalAmount?: number } }[];
  supplierGroup?: LedgerPoGroup | null;
}

/** Credit payouts (method 3) are not money paid yet. */
const CREDIT = 3;

/** "10 CWP, 5 PCS": each quantity with its unit, in qty type order (no total across units). */
export const poQuantityText = (
  po: Pick<LedgerPo, "totalQuantities">
): string => {
  const byType = new Map<number, number>();
  (po.totalQuantities || []).forEach((q) => {
    const t = Number(q.quantityType);
    byType.set(t, (byType.get(t) || 0) + (Number(q.stockCount) || 0));
  });
  return (
    [...byType.entries()]
      .filter(([, n]) => n > 0)
      .sort(([a], [b]) => a - b)
      .map(([t, n]) => Method.formatQuantityDisplay(t, n))
      .filter(Boolean)
      .join(", ") || "-"
  );
};

export const poPaid = (po: LedgerPo): number => {
  const payout = po.businesses?.[0]?.payout;
  return payout?.paymentMethod === CREDIT
    ? 0
    : Number(payout?.totalAmount) || 0;
};

export const poRemaining = (po: LedgerPo): number =>
  (Number(po.charge) || 0) - poPaid(po);

/** "" = the supplier itself (no group) */
export const poGroupKey = (po: LedgerPo): string =>
  String(po.supplierGroup?.reference || "");

export const poGroupName = (po: LedgerPo, supplierName = "Supplier"): string =>
  po.supplierGroup?.name || `${supplierName} (no group)`;

export interface LedgerGroupSummary {
  key: string;
  name: string;
  orders: number;
  total: number;
  paid: number;
  remaining: number;
  /** contact / TIN / first bank of the group at PO submit (empty for no group) */
  details: string;
}

/** Totals per supplier group, groups first (by name), the supplier's own POs last. */
export const ledgerGroupSummaries = (
  pos: LedgerPo[],
  supplierName = "Supplier"
): LedgerGroupSummary[] => {
  const map = new Map<string, LedgerGroupSummary>();
  pos.forEach((po) => {
    const key = poGroupKey(po);
    const g = po.supplierGroup;
    const cur = map.get(key) ?? {
      key,
      name: poGroupName(po, supplierName),
      orders: 0,
      total: 0,
      paid: 0,
      remaining: 0,
      details: g
        ? [
            g.contactName,
            g.phone,
            g.tinNumber ? `TIN ${g.tinNumber}` : "",
            g.banks?.[0]?.accountNumber
              ? `${g.banks[0].bankName || "Bank"} ${g.banks[0].accountNumber}`
              : "",
          ]
            .filter(Boolean)
            .join(" · ")
        : "",
    };
    cur.orders += 1;
    cur.total += Number(po.charge) || 0;
    cur.paid += poPaid(po);
    cur.remaining += poRemaining(po);
    map.set(key, cur);
  });
  return [...map.values()].sort(
    (a, b) =>
      Number(a.key === "") - Number(b.key === "") ||
      a.name.localeCompare(b.name)
  );
};

/** True when the POs belong to more than one group (or mix a group with the supplier itself). */
export const mixesGroups = (pos: LedgerPo[]): boolean =>
  new Set(pos.map(poGroupKey)).size > 1;
